// @flow
import React, { useCallback, useMemo, useRef } from 'react';
import { RightSidePanel, FlexContainer } from '@atlaskit/right-side-panel';
import Page, { Grid, GridColumn } from '@atlaskit/page';
import { v4 as uuidV4 } from 'uuid';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { NetworkStatus } from 'apollo-client';
import { Query, compose } from 'react-apollo';
import Button from '@atlaskit/button';
import { useSetState } from 'react-use';
import styled from 'styled-components';
import VirtualComponents from './VirtualComponents';
import VirtualComponentReleases from './VirtualComponentReleases';
import { analytics } from '../services/analytics';
import { OCTO_GLOBAL_PERMISSIONS } from '../utils/consts';
import OctoPermissionsContext from '../providers/OctoPermissionsContext';
import { hasPermission, hasPermissionArray } from '../utils/commons';
import { GET_PERMISSIONS, SYNC_ERRORS } from '../graphql/queries';
import VirtualComponentHierarchyPage from './VirtualComponentHierarchy';
import NoPermission from '../components/no-permission/NoPermission';
import VirtualComponentPageHeader from './VirtualComponentPageHeader';
import Loader from '../components/utils/Loader/Loader';
import { withCreateVirtualComponentContext } from '../contexts/createVirtualComponentContext';
import HelpMenu from '../components/HelpMenu';
import ExportVirtualComponentsButton from '../components/virtual-components/ExportCSVButton';
import ExportComponentReleasesButton from '../components/virtual-component-releases/ExportCSVButton';
import { withCreateComponentReleaseContext } from '../contexts/createComponentReleaseContext';
import useCreateVirtualComponentContext from '../hooks/useVirtualComponentCreation';
import useCreateComponentReleaseContext from '../hooks/useComponentReleaseCreation';
import { withFlagContext } from '../contexts/flagContext';
import useJiraContext from '../hooks/useJiraContext';

const COMPONENTS_TAB = 0;
const COMPONENTS_HIERARCHY_TAB = 1;

const Wrapper = styled.div`
    [class$='-GridColumn'] {
        max-width: 100%;
    }
    div:has(div[id='attach']) {
        max-width: 100%;
    }
`;

const VirtualComponentPage = ({ permissions, syncErrorsQueryData }) => {
    const [state, setState] = useSetState({
        isRightSidePanelShowed: false,
        isSyncErrorsModalOpen: false,
        rightSidePanelContent: null,
        selectedTabIndex: 0,
        refetchKey: null,
        synchronizeKey: null,
        componentVersionsFilterVariables: null,
    });
    const { projectKey } = useJiraContext();

    const { show: displayCreateVirtualComponentModal } = useCreateVirtualComponentContext();
    const { show: displayCreateComponentReleaseModal } = useCreateComponentReleaseContext();

    const openCreateVirtualComponentModal = useCallback(() => {
        const onSuccessCallback = () => setState({ refetchKey: uuidV4() });
        displayCreateVirtualComponentModal(onSuccessCallback);
    }, [displayCreateVirtualComponentModal, setState]);

    const openCreateComponentReleaseModal = useCallback(() => {
        const onSuccessCallback = () => setState({ refetchKey: uuidV4() });
        displayCreateComponentReleaseModal(onSuccessCallback);
    }, [displayCreateComponentReleaseModal, setState]);

    const requestSynchronize = useCallback(() => setState({ synchronizeKey: uuidV4() }), [setState]);

    const [putContentToRightSidePanel, showRightSidePanel, hideRightSidePanel] = useMemo(
        () => [
            (jsx, onStateChanged = null, showStatus = null) => {
                if (showStatus !== null) {
                    setState(
                        {
                            rightSidePanelContent: jsx,
                            isRightSidePanelShowed: showStatus,
                        },
                        onStateChanged,
                    );
                } else {
                    setState(
                        {
                            rightSidePanelContent: jsx,
                        },
                        onStateChanged,
                    );
                }
            },
            (onStateChanged = null) => {
                setState(
                    {
                        isRightSidePanelShowed: true,
                    },
                    onStateChanged,
                );
            },
            (onStateChanged = null) => {
                setState(
                    {
                        isRightSidePanelShowed: false,
                        rightSidePanelContent: null,
                    },
                    onStateChanged,
                );
            },
        ],
        [setState],
    );

    const handleTabChange = useCallback(
        (index) => {
            // Release Tab accessed
            if (index === 1) {
                analytics.track({
                    userId: window.license,
                    event: 'accessed_component_releases_tab',
                    timestamp: new Date(),
                    properties: {
                        addon_key: process.env.REACT_APP_KEY,
                    },
                });
            }
            setState({
                selectedTabIndex: index,
                isRightSidePanelShowed: false,
                activeTabHandler: null,
            });
        },
        [setState],
    );

    const filterVariablesRef = useRef({});

    const onSearch = useCallback((variables) => {
        filterVariablesRef.current = variables;
    }, []);

    const onComponentReleasesFilterChange = useCallback(
        (variables) => {
            setState({
                componentVersionsFilterVariables: variables,
            });
        },
        [setState],
    );

    const tabs = useMemo(() => {
        const actions = {
            createVirtualComponent: {
                key: 'create-virtual-component',
                visible: hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions),
                render: () => (
                    <Button className="button" appearance="primary" onClick={openCreateVirtualComponentModal}>
                        Create component
                    </Button>
                ),
            },
            exportVirtualComponents: {
                key: 'export-virtual-components',
                visible:
                    !projectKey &&
                    hasPermissionArray([OCTO_GLOBAL_PERMISSIONS.MANAGE, OCTO_GLOBAL_PERMISSIONS.VIEW], permissions),
                render: () => <ExportVirtualComponentsButton searchRef={filterVariablesRef} />,
            },
            helpMenu: { key: 'help-menu', visible: true, render: () => <HelpMenu /> },
            createComponentRelease: {
                key: 'create-component-release',
                visible: hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions),
                render: () => (
                    <Button className="button" appearance="primary" onClick={openCreateComponentReleaseModal}>
                        Create component version
                    </Button>
                ),
            },
            exportComponentReleases: {
                key: 'export-component-releases',
                visible: hasPermissionArray(
                    [OCTO_GLOBAL_PERMISSIONS.MANAGE, OCTO_GLOBAL_PERMISSIONS.VIEW],
                    permissions,
                ),
                render: () => (
                    <ExportComponentReleasesButton filterVariables={state.componentVersionsFilterVariables} />
                ),
            },
        };
        return [
            {
                label: 'Components',
                content: (
                    <div
                        style={{
                            paddingTop: '25px',
                            width: '100%',
                        }}
                    >
                        <VirtualComponents
                            isRightSidePanelShowed={state.isRightSidePanelShowed}
                            putContentToRightSidePanel={putContentToRightSidePanel}
                            synchronize={requestSynchronize}
                            showRightSidePanel={showRightSidePanel}
                            hideRightSidePanel={hideRightSidePanel}
                            refetchKey={state.refetchKey}
                            onSearch={onSearch}
                        />
                    </div>
                ),
                tooltip: 'Components',
                title: 'Component management',
                actions: [actions.createVirtualComponent, actions.exportVirtualComponents, actions.helpMenu],
            },
            {
                label: 'Component hierarchy',
                content: (
                    <div
                        style={{
                            paddingTop: '25px',
                            width: '100%',
                        }}
                    >
                        <VirtualComponentHierarchyPage
                            isRightSidePanelShowed={state.isRightSidePanelShowed}
                            putContentToRightSidePanel={putContentToRightSidePanel}
                            synchronize={requestSynchronize}
                            showRightSidePanel={showRightSidePanel}
                            hideRightSidePanel={hideRightSidePanel}
                        />
                    </div>
                ),
                tooltip: 'Component hierarchy',
                title: 'Component hierarchy',
                actions: [actions.createVirtualComponent, actions.helpMenu],
            },
            {
                label: 'Component versions',
                content: (
                    <div
                        style={{
                            paddingTop: '25px',
                            width: '100%',
                        }}
                    >
                        <VirtualComponentReleases
                            isRightSidePanelShowed={state.isRightSidePanelShowed}
                            putContentToRightSidePanel={putContentToRightSidePanel}
                            showRightSidePanel={showRightSidePanel}
                            hideRightSidePanel={hideRightSidePanel}
                            refetchKey={state.refetchKey}
                            openCreateComponentReleaseModal={openCreateComponentReleaseModal}
                            onComponentReleasesFilterChange={onComponentReleasesFilterChange}
                        />
                    </div>
                ),
                tooltip: 'Component versions',
                title: 'Component versions',
                actions: [actions.createComponentRelease, actions.exportComponentReleases, actions.helpMenu],
            },
        ];
    }, [
        permissions,
        projectKey,
        state.isRightSidePanelShowed,
        state.refetchKey,
        state.componentVersionsFilterVariables,
        putContentToRightSidePanel,
        requestSynchronize,
        showRightSidePanel,
        hideRightSidePanel,
        onSearch,
        openCreateComponentReleaseModal,
        onComponentReleasesFilterChange,
        openCreateVirtualComponentModal,
    ]);

    if (!window) {
        return <Loader size="small" />;
    }

    const tabConfig = tabs[state.selectedTabIndex];

    return (
        <OctoPermissionsContext.Provider value={permissions}>
            <Page>
                <Wrapper>
                    <Grid layout="fluid">
                        <GridColumn medium={12}>
                            <FlexContainer id="attach">
                                <div style={{ padding: '0 24px', width: '100%' }}>
                                    {
                                        <VirtualComponentPageHeader
                                            syncErrorsQueryData={syncErrorsQueryData}
                                            displaySynchronizedStatus={[
                                                COMPONENTS_TAB,
                                                COMPONENTS_HIERARCHY_TAB,
                                            ].includes(state.selectedTabIndex)}
                                            pageTitle={tabConfig.title}
                                            actions={tabConfig.actions}
                                            synchronizeKey={state.synchronizeKey}
                                        />
                                    }
                                    {projectKey && (
                                        <VirtualComponents
                                            isRightSidePanelShowed={state.isRightSidePanelShowed}
                                            putContentToRightSidePanel={putContentToRightSidePanel}
                                            synchronize={requestSynchronize}
                                            showRightSidePanel={showRightSidePanel}
                                            hideRightSidePanel={hideRightSidePanel}
                                            projectKey={projectKey}
                                            refetchKey={state.refetchKey}
                                            onSearch={onSearch}
                                        />
                                    )}
                                    {!projectKey && (
                                        <Tabs
                                            selected={state.selectedTabIndex}
                                            onChange={handleTabChange}
                                            id="virtual-components-tabs"
                                            shouldUnmountTabPanelOnChange
                                        >
                                            <TabList>
                                                {tabs.map((tab, index) => (
                                                    <Tab testId={tab.testId} key={index}>
                                                        {tab.label}
                                                    </Tab>
                                                ))}
                                            </TabList>
                                            {tabs.map((tab, index) => (
                                                <TabPanel key={index}>{tab.content}</TabPanel>
                                            ))}
                                        </Tabs>
                                    )}
                                </div>
                                <RightSidePanel
                                    attachPanelTo="attach"
                                    isOpen={state.isRightSidePanelShowed}
                                    // eslint-disable-next-line react/no-children-prop
                                    children={state.rightSidePanelContent}
                                />
                            </FlexContainer>
                        </GridColumn>
                    </Grid>
                </Wrapper>
            </Page>
        </OctoPermissionsContext.Provider>
    );
};

const withSyncErrorsQuery = (Component) => {
    const WithSyncErrorsQuery = (props) => {
        return (
            <Query displayName="SyncErrorsQuery" query={SYNC_ERRORS} notifyOnNetworkStatusChange={true}>
                {(query) => {
                    if (query.loading || query.networkStatus === NetworkStatus.refetch) return <Loader size="small" />;
                    if (query.error) return `Error! ${query.error.message}`;
                    return <Component {...props} syncErrorsQueryData={query.data} />;
                }}
            </Query>
        );
    };
    return WithSyncErrorsQuery;
};

const withJiraPermissionsQuery = (Component) => {
    const WithJiraPermissionsQuery = (props) => {
        return (
            <Query displayName="JiraPermissionsQuery" query={GET_PERMISSIONS}>
                {(query) => {
                    if (query.loading || query.networkStatus === NetworkStatus.refetch) return <Loader size="small" />;
                    if (query.error) return `Error! ${query.error.message}`;
                    if (
                        !hasPermissionArray(
                            [OCTO_GLOBAL_PERMISSIONS.MANAGE, OCTO_GLOBAL_PERMISSIONS.VIEW],
                            query.data.getPermissions,
                        )
                    ) {
                        return <NoPermission />;
                    }
                    return <Component {...props} permissions={query.data.getPermissions} />;
                }}
            </Query>
        );
    };
    return WithJiraPermissionsQuery;
};

export default compose(
    withFlagContext,
    withSyncErrorsQuery,
    withJiraPermissionsQuery,
    withCreateVirtualComponentContext,
    withCreateComponentReleaseContext,
)(VirtualComponentPage);
